import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { SettingsCard } from "../../../../components/card/SettingsCard";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import PlusSvg from "../../../../components/svg/PlusSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useAddEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useGetEmailTemplatesQuery,
  useMarkEmailTemplateActiveMutation,
  useUpdateEmailTemplateMutation,
} from "../../../../slices/EmailTemplatesSlice";
import { EmailTemplateModal } from "./EmailTemplateModal";
import { EmailTemplate, EmailTemplatePayload } from "./emailTemplates.types";
import { EmailTemplatesTable } from "./EmailTemplatesTable";

export const EmailTemplates: React.FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const user = useAppSelector((state) => state.appReducer.user);
  const { data: templates = [], isFetching: isLoadingTemplates } =
    useGetEmailTemplatesQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );
  const [addEmailTemplate, { isLoading: isAddingEmailTemplate }] =
    useAddEmailTemplateMutation();
  const [updateEmailTemplate, { isLoading: isUpdatingEmailTemplate }] =
    useUpdateEmailTemplateMutation();
  const [deleteEmailTemplate, { isLoading: isDeletingEmailTemplate }] =
    useDeleteEmailTemplateMutation();
  const [markEmailTemplateActive] = useMarkEmailTemplateActiveMutation();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [isOpenTemplateModal, setIsOpenTemplateModal] = useState(false);
  const [openTemplateDelete, setOpenTemplateDelete] =
    useState<EmailTemplate | null>(null);

  const handleOpenCreateModal = () => {
    setEditingTemplate(null);
    setIsOpenTemplateModal(true);
  };

  const handleOpenEditModal = useCallback((template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsOpenTemplateModal(true);
  }, []);

  const handleCloseTemplateModal = () => {
    setEditingTemplate(null);
    setIsOpenTemplateModal(false);
  };

  const handleSubmitTemplate = async (values: EmailTemplatePayload) => {
    if (!orgId || !user?.id) {
      toast("Unable to save email template without an active organization", {
        type: "error",
      });
      return;
    }

    try {
      if (editingTemplate) {
        await updateEmailTemplate({
          template: editingTemplate,
          values,
          orgId,
          userId: user.id,
        }).unwrap();
        toast("Email template successfully updated", { type: "success" });
      } else {
        await addEmailTemplate({
          values: { ...values, isActive: true },
          orgId,
          userId: user.id,
        }).unwrap();
        toast("Email template successfully created", { type: "success" });
      }

      handleCloseTemplateModal();
    } catch (error) {
      console.error("Failed to save email template", error);
      toast("Failed to save email template", { type: "error" });
    }
  };

  const handleMarkTemplateActive = useCallback(
    async (template: EmailTemplate) => {
      try {
        await markEmailTemplateActive({
          id: template.id,
          isActive: !template.isActive,
        }).unwrap();
        toast("Email template successfully updated", { type: "success" });
      } catch (error) {
        console.error("Failed to update email template status", error);
        toast("Failed to update email template status", { type: "error" });
      }
    },
    [markEmailTemplateActive],
  );

  const handleDeleteTemplate = async () => {
    if (!openTemplateDelete) {
      return;
    }

    if (!orgId) {
      toast("Unable to delete email template without an active organization", {
        type: "error",
      });
      return;
    }

    try {
      await deleteEmailTemplate({
        id: openTemplateDelete.id,
        orgId,
      }).unwrap();
      toast("Email template successfully deleted", { type: "success" });
      setOpenTemplateDelete(null);
    } catch (error) {
      console.error("Failed to delete email template", error);
      toast("Failed to delete email template", { type: "error" });
    }
  };

  return (
    <>
      <div className={"mb-[120px] py-6"}>
        <div
          className={
            "mb-6 flex items-center justify-between gap-4 font-Manrope"
          }>
          <div className={"text-2xl font-semibold"}>Email Templates</div>

          <div className={"flex gap-4"}>
            <button
              onClick={handleOpenCreateModal}
              className={
                "flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-[#6938EF] px-4 text-sm font-medium text-white"
              }>
              <PlusSvg />
              <span>New Template</span>
            </button>
          </div>
        </div>

        <SettingsCard>
          {isLoadingTemplates ? (
            <div className={"p-6 text-sm text-[#5F758F]"}>
              Loading email templates...
            </div>
          ) : templates.length === 0 ? (
            <div
              className={"p-6 text-center text-sm font-medium text-[#5F758F]"}>
              There are no email templates
            </div>
          ) : (
            <EmailTemplatesTable
              templates={templates}
              onEdit={handleOpenEditModal}
              onToggleActive={handleMarkTemplateActive}
              onDelete={setOpenTemplateDelete}
            />
          )}
        </SettingsCard>
      </div>

      {isOpenTemplateModal && (
        <EmailTemplateModal
          template={editingTemplate}
          onClose={handleCloseTemplateModal}
          onSubmit={handleSubmitTemplate}
          isSubmitting={isAddingEmailTemplate || isUpdatingEmailTemplate}
        />
      )}

      {openTemplateDelete && (
        <SettingsConfirmModal
          onClose={() => setOpenTemplateDelete(null)}
          onConfirm={handleDeleteTemplate}
          icon={<TrashSvg />}
          title={`Delete ‘${openTemplateDelete.name}’ template?`}
          description={"Do you want to permanently delete this template?"}
          confirmText={isDeletingEmailTemplate ? "Deleting..." : "Delete"}
          variant={"danger"}
        />
      )}
    </>
  );
};
